/**
 * FullscreenLock (Sprint 6 B5).
 *
 * Behavior:
 *   - Mount → request fullscreen via Fullscreen API.
 *   - Listen `fullscreenchange` → khi user exit (Esc / F11 / window switch):
 *       1. Log violation HIGH `fullscreen_exit`
 *       2. Show modal block content + button "Vào lại fullscreen"
 *   - Unmount → exit fullscreen + cleanup listener.
 *
 * Browser compat:
 *   - Chrome 71+, Firefox 64+, Safari 16.4+ (iPad)
 *   - iOS Safari < 16.4 không support → fallback log violation HIGH ngay từ đầu.
 */

import { useEffect, useState } from "react";
import { Button, Modal, Typography } from "antd";
import { FullscreenOutlined } from "@ant-design/icons";

const { Title, Paragraph } = Typography;

interface Props {
  onViolation: (event: { type: "fullscreen_exit"; severity: "high"; detail?: Record<string, unknown> }) => void;
  /** Show blocking modal khi exit. False = chỉ log không block (test mode). */
  blockOnExit?: boolean;
}

function requestFullscreen(): Promise<void> {
  const el = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
  };
  if (el.requestFullscreen) return el.requestFullscreen();
  if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
  return Promise.reject(new Error("Fullscreen API not supported"));
}

function exitFullscreen(): Promise<void> {
  const doc = document as Document & {
    webkitExitFullscreen?: () => Promise<void>;
  };
  if (document.fullscreenElement && document.exitFullscreen) {
    return document.exitFullscreen();
  }
  if (doc.webkitExitFullscreen) return doc.webkitExitFullscreen();
  return Promise.resolve();
}

export function FullscreenLock({ onViolation, blockOnExit = true }: Props) {
  const [showBlocker, setShowBlocker] = useState(false);
  const [notSupported, setNotSupported] = useState(false);

  useEffect(() => {
    requestFullscreen().catch(() => {
      setNotSupported(true);
      onViolation({
        type: "fullscreen_exit",
        severity: "high",
        detail: { reason: "api_not_supported" },
      });
    });

    const handleChange = () => {
      if (!document.fullscreenElement) {
        onViolation({
          type: "fullscreen_exit",
          severity: "high",
          detail: { at: new Date().toISOString() },
        });
        if (blockOnExit) setShowBlocker(true);
      }
    };
    document.addEventListener("fullscreenchange", handleChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleChange);
      void exitFullscreen();
    };
  }, [onViolation, blockOnExit]);

  if (notSupported) {
    return (
      <Modal
        open
        closable={false}
        footer={null}
        maskClosable={false}
        title="Trình duyệt không hỗ trợ chế độ toàn màn hình"
      >
        <Paragraph>
          Bài thi của bạn yêu cầu chế độ toàn màn hình để chống gian lận. Vui lòng
          dùng <strong>Chrome 71+</strong>, <strong>Firefox 64+</strong>, hoặc{" "}
          <strong>Safari 16.4+</strong>.
        </Paragraph>
      </Modal>
    );
  }

  return (
    <Modal
      open={showBlocker}
      closable={false}
      maskClosable={false}
      footer={null}
      title={
        <Title level={4} style={{ color: "#cf1322", margin: 0 }}>
          ⚠️ Bạn vừa thoát chế độ toàn màn hình
        </Title>
      }
      data-testid="fullscreen-blocker"
    >
      <Paragraph>
        Hành vi này đã được ghi lại. Tiếp tục vi phạm có thể dẫn đến{" "}
        <strong>huỷ kết quả thi</strong>.
      </Paragraph>
      <Button
        type="primary"
        icon={<FullscreenOutlined />}
        size="large"
        block
        onClick={async () => {
          try {
            await requestFullscreen();
            setShowBlocker(false);
          } catch {
            /* user cancel — keep blocker */
          }
        }}
      >
        Vào lại chế độ toàn màn hình
      </Button>
    </Modal>
  );
}
